// This component has several `position: fixed` overlays (the tour card, the node inspector drawer,
// the fullscreen modal backdrop) that need to escape whatever ancestor's `transform`/`filter`/
// `backdrop-filter`/`perspective` would otherwise reposition them — CSS containment rules a `fixed`
// element obeys relative to the nearest such ancestor, not the true viewport, if one exists between
// the element and <body>. This component is nested inside a Bootstrap modal whose chrome uses
// backdrop-filter, which silently trapped "fixed" elements inside the modal's box instead of the
// true viewport. Moving these overlays to be children of <body> on connect sidesteps the whole class
// of bug regardless of which ancestor is responsible.
//
// Extracted out of the topology Stimulus controller alongside tour.ts and inspector.ts.
export class PortaledOverlays {
    constructor(host, 
    // The controller element's own class list, so the portal root carries every scoped rule this
    // component's stylesheet defines (colors, --bg-main, .tour-popup, .inspector-drawer, ...) — all
    // SCSS-nested under `.topology-legacy-wrapper`, which is the controller element itself. Moving
    // an overlay straight to <body> without this would take it outside that selector's reach and
    // strip all of it, not just positioning.
    rootClassName) {
        this.host = host;
        this.rootClassName = rootClassName;
        this.portaledOverlays = [];
        this.portalRoot = null;
        // Listeners rebind() attaches directly to DOM elements (rather than through Stimulus's own
        // data-action wiring, which they bypass — see rebind() below). Stimulus does not know about these,
        // so nothing removes them automatically on disconnect; tracked here so destroy() can.
        this.reboundListeners = [];
    }
    portalToBody(overlays) {
        if (overlays.every(el => !el))
            return;
        const portalRoot = document.createElement('div');
        // ...plus a dedicated opt-out for the wrapper's PAGE-LEVEL box model (UX review F4,
        // 2026-09-19). The class list above must come along so the scoped rules still reach these
        // overlays — but it carries `.topology-legacy-wrapper`'s min-height: 100vh, --bg-main
        // background and 1.15rem scale with it, and as an empty div appended to <body> that is a full
        // viewport of dead scroll at the end of every topology page and ~15%-larger text inside the
        // modal. topology-legacy-portal reverses exactly those three (see _topology_legacy.scss).
        portalRoot.className = `${this.rootClassName} topology-legacy-portal`;
        document.body.appendChild(portalRoot);
        this.portalRoot = portalRoot;
        overlays.forEach(el => {
            if (!el)
                return;
            const homeParent = el.parentElement;
            if (!homeParent)
                return;
            this.portaledOverlays.push({ el, homeParent, homeNextSibling: el.nextSibling });
            portalRoot.appendChild(el);
            this.rebind(el);
        });
    }
    // Stimulus resolves a `data-action="click->topology#foo"` binding to a controller instance by
    // walking up the DOM from the clicked element to find the nearest `data-controller="topology"`
    // ancestor. Once an overlay is portaled to <body>, it no longer has one — the original controller
    // element is a sibling now, not an ancestor — so every button inside it (Next/Previous, the ×
    // close buttons, the fullscreen modal's zoom controls) would silently stop firing even though the
    // position/target-lookup fixes above are in place. Re-attaching the same `data-action="x->topology#y"`
    // bindings as plain listeners bound directly to the host instance sidesteps that; the target
    // methods already read their params off `event.currentTarget.dataset`, so no method bodies change.
    rebind(container) {
        container.querySelectorAll('[data-action]').forEach(el => {
            const actionAttr = el.getAttribute('data-action') || '';
            actionAttr.split(/\s+/).forEach(binding => {
                const match = binding.match(/^([a-z:]+)(?:@\w+)?->topology#(\w+)$/);
                if (!match)
                    return;
                const [, eventName, methodName] = match;
                const handler = this.host[methodName];
                if (typeof handler !== 'function')
                    return;
                // Named so it can be removed again in destroy() — an inline arrow function passed straight
                // to addEventListener has no reference removeEventListener could match.
                const boundHandler = (event) => handler.call(this.host, event);
                el.addEventListener(eventName, boundHandler);
                this.reboundListeners.push({ el, eventName, handler: boundHandler });
            });
        });
    }
    // Counterpart to rebind(): these listeners are plain addEventListener calls, not Stimulus
    // data-action bindings, so nothing removes them automatically when the controller disconnects.
    // Left in place, a portaled element that survives a Turbo morph reconnect and gets rebound again
    // would carry every prior connect's handlers alongside the new one.
    destroy() {
        this.reboundListeners.forEach(({ el, eventName, handler }) => el.removeEventListener(eventName, handler));
        this.reboundListeners = [];
        this.portaledOverlays.forEach(({ el, homeParent, homeNextSibling }) => {
            if (el.parentElement !== this.portalRoot)
                return;
            if (homeNextSibling) {
                homeParent.insertBefore(el, homeNextSibling);
            }
            else {
                homeParent.appendChild(el);
            }
        });
        this.portaledOverlays = [];
        if (this.portalRoot) {
            this.portalRoot.remove();
            this.portalRoot = null;
        }
    }
}
